import { parseAsString, parseAsStringLiteral, useQueryState, useQueryStates } from 'nuqs';
import { RequirePermission } from '@/app/require-permission';
import { PageHeader } from '@/components/shared/page-header';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBarbers } from '@/lib/org';
import { PeriodFilter, usePeriodParams } from '../period';
import { UnitSelect, useUnitParam } from '../unit';
import { ExpensesTab } from './expenses-tab';
import { ExportButton, Overview } from './overview';

const TABS = ['geral', 'despesas'] as const;

function ReportsContent() {
  const period = usePeriodParams();
  const unit = useUnitParam(false);
  const barbers = useBarbers();
  const [barberId, setBarberId] = useQueryState('barbeiro', parseAsString.withOptions({ history: 'replace' }));
  const [tab, setTab] = useQueryState('aba', parseAsStringLiteral(TABS).withDefault('geral').withOptions({ history: 'replace' }));
  const [, setShared] = useQueryStates({ page: parseAsString, q: parseAsString, sort: parseAsString, order: parseAsString }, { history: 'replace' });

  const barberList = (barbers.data ?? []).filter((b) => !unit.branchId || b.branchId === unit.branchId);
  const filters = { from: period.from, to: period.to, branchId: unit.branchId ?? undefined, barberId: barberId ?? undefined };

  return (
    <>
      <PageHeader
        title="Relatórios"
        description="Faturamento, recebimentos, despesas e desempenho da equipe no período."
        actions={tab === 'geral' ? <ExportButton f={filters} /> : undefined}
      />
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <PeriodFilter from={period.from} to={period.to} tz={period.tz} onChange={(v) => { period.set(v); void setShared({ page: null }); }} />
        <UnitSelect branches={unit.branches} value={unit.branchId} onChange={(id) => { void unit.setBranchId(id); void setBarberId(null); }} allowAll />
        {tab === 'geral' && (
          <Select value={barberId ?? 'all'} onValueChange={(v) => setBarberId(v === 'all' ? null : v)}>
            <SelectTrigger aria-label="Profissional" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os profissionais</SelectItem>
              {barberList.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <Tabs
        value={tab}
        onValueChange={(v) => {
          void setShared({ page: null, q: null, sort: null, order: null });
          void setTab(v as (typeof TABS)[number]);
        }}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="geral">Visão geral</TabsTrigger>
          <TabsTrigger value="despesas">Despesas</TabsTrigger>
        </TabsList>
        <TabsContent value="geral">
          <Overview f={filters} />
        </TabsContent>
        <TabsContent value="despesas">
          <ExpensesTab from={period.from} to={period.to} branchId={unit.branchId} tz={period.tz} />
        </TabsContent>
      </Tabs>
    </>
  );
}

export function ReportsPage() {
  return (
    <RequirePermission anyOf={['reports.read']}>
      <ReportsContent />
    </RequirePermission>
  );
}
